import { X } from "@tamagui/lucide-icons";
import React from "react";
import {
  type ModalProps,
  Pressable,
  Modal as RNModal,
  ScrollView,
} from "react-native";
import { useWindowDimensions, View } from "tamagui";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { trackEvent } from "../core/trackEvent";

const getIsIos = () => {
  const platform = window?.navigator?.platform ?? "web";
  const iosPlatforms = ["iPhone", "iPad", "iPod"];

  return iosPlatforms.indexOf(platform) !== -1;
};

export const Modal: React.FC<
  ModalProps & {
    modalId: string;
    onClose: () => void;
    noPadding?: boolean;
    header?: JSX.Element;
    footer?: JSX.Element;
    footerOptions?: {
      noPaddingTop?: boolean;
    };
    // biome-ignore lint/suspicious/noExplicitAny: Didn't work in tests
    justifyContent?: any;
  }
> = ({
  modalId,
  header,
  footer,
  footerOptions,
  children,
  visible,
  noPadding,
  onClose,
  justifyContent,
  ...props
}) => {
  const hasContent = !!children;
  const padding = noPadding ? undefined : "$space.large";
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const keyboardShowing = useDetectKeyboardOpen();

  const scrollViewRef = React.useRef<ScrollView>(null);
  const isIos = getIsIos();

  const _keyboardMagic = isIos && keyboardShowing;
  const showFooter = !isIos || !keyboardShowing;

  /*
  React.useEffect(() => {
    if (keyboardMagic) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd();
      }, 500);
    }
  }, [keyboardMagic]);
*/

  React.useEffect(() => {
    if (visible) {
      trackEvent(`modal_opened_${modalId}`);

      return () => {
        trackEvent(`modal_closed_${modalId}`);
      };
    }
  }, [visible, modalId]);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={(_e) => {
        onClose();
      }}
      {...props}
    >
      <View
        className="scrollbar-stable"
        flex={1}
        backgroundColor={"rgba(0,0,0,0.7)"}
        justifyContent={justifyContent ?? "center"}
      >
        <View
          width={"100%"}
          maxWidth={Math.min(600, windowWidth - 18 * 2)}
          maxHeight={windowHeight - 18 * 2}
          alignSelf="center"
          backgroundColor={"$inverted"}
          margin="$space.large"
        >
          {header && (
            <View
              paddingTop={padding}
              paddingHorizontal={padding}
              backgroundColor={"$backgroundBlock"}
            >
              <View
                paddingBottom="$space.medium"
                borderBottomWidth={1}
                borderBottomColor={"$divider"}
              >
                {header}
              </View>
            </View>
          )}
          <ScrollView ref={scrollViewRef} style={{ flexGrow: 1 }}>
            {hasContent && (
              <View
                flex={1}
                paddingHorizontal={padding}
                paddingTop={header && !noPadding ? "$space.medium" : padding}
                paddingBottom={footer && !noPadding ? "$space.medium" : padding}
              >
                {children}
              </View>
            )}
          </ScrollView>

          {footer && showFooter && (
            <View paddingHorizontal={padding}>
              <View
                paddingBottom={padding}
                paddingTop={
                  footerOptions?.noPaddingTop
                    ? 0
                    : footer
                      ? "$space.medium"
                      : padding
                }
                borderTopWidth={hasContent ? 1 : 0}
                borderTopColor={"$divider"}
              >
                {footer}
              </View>
            </View>
          )}

          <View
            position="absolute"
            right={0}
            top={0}
            backgroundColor={"#background"}
          >
            <Pressable
              onPress={() => onClose()}
              style={{ paddingRight: 10, paddingTop: 10, zIndex: 21 }}
            >
              <X size={"$size.medium"} />
            </Pressable>
          </View>
        </View>
      </View>
    </RNModal>
  );
};
